import { Inject, ReflectiveInjector } from '@angular/core';
import { isDefined } from 'ionic-angular/util/util';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { CHANGE_LOG_STORAGE_TOKEN } from '../../../app/injection-tokens';
import { User } from '../../../vas/business-objects/user/user.model';
import { asyncForEach } from '../../util/async-for-each';
import { isInArray } from '../../util/is-in-array';
import { IStorage } from '../storage/storage.interface';
import { ChangeQItemHelper } from '../sync-up/change-q-item.helper';
import { FileChangeQItem } from '../sync-up/file-item';
import { ChangeQItem } from '../sync-up/item';
import { ReportLayoutChangeQItem } from '../sync-up/report-layout-item';

export class ChangeLogService {

    static readonly CLASS: string = 'ChangeLogService';

    static changesWaiting$: BehaviorSubject<number> =
        new BehaviorSubject(undefined);

    static _self: ChangeLogService;

    protected _isChangesQueueProcessing = false;
    protected activeUser: User;

    protected _insertQ: Array<{key: string, item: ChangeQItem}> = [];
    protected _insertQInterval: any;

    protected indexById: {[objectId: string]: Array<string>} = {};

    protected _itemPosition = 0;

    static getInstance(): ChangeLogService {

        if (!ChangeLogService._self) {
            const injector = ReflectiveInjector.resolveAndCreate([
                {
                    provide: ChangeLogService,
                    useClass: ChangeLogService,
                    deps: [CHANGE_LOG_STORAGE_TOKEN]
                }
            ]);
            ChangeLogService._self = injector.get(ChangeLogService);
        }

        return ChangeLogService._self;
    }

    constructor(
        @Inject(CHANGE_LOG_STORAGE_TOKEN) protected storage: IStorage
    ) {
        if (!isDefined(ChangeLogService._self)) {
            ChangeLogService._self = this;
        }

        this.indexInit();
    }

    get itemPosition(): number {
        return this._itemPosition;
    }

    get nextItemPosition(): number {
        this._itemPosition++;
        return this._itemPosition;
    }

    protected getStorage(): IStorage {
        return this.storage;
    }

    /**
     * Clears all items
     */
    async clear(): Promise<any> {
        await this.getStorage().clear();
        await this.refreshCount();
        await this.clearIndex();
    }

    /**
     * Returns a count of the number of items
     */
    getCount(): Promise<number> {
        return this.getStorage().length();
    }

    get isChangesQueueProcessing(): boolean {
        return this._isChangesQueueProcessing;
    }

    /**
     * Refreshes the item count
     */
    protected async refreshCount(): Promise<void> {
        const count = await this.getCount();
        ChangeLogService.changesWaiting$.next(count);
    }

    enqueueItem(key: string, item: ChangeQItem): void {
        this._insertQ.push({key, item});
        clearInterval(this._insertQInterval);
        this._insertQInterval = setTimeout(() => {
            this.addItemsFromQ();
        }, 100);
    }

    protected addItemsFromQ(): void {
        this._isChangesQueueProcessing = true;
        const q = this._insertQ;
        this._insertQ = [];

        asyncForEach(q, async qItem => {
            try {
                await this.addItem(qItem.key, qItem.item);
            } catch (error) {
                console.warn(error);
            }
        }).then(
            () => this._isChangesQueueProcessing = false,
            error => {
                console.error(error);
                this._isChangesQueueProcessing = false;
            }
        );
    }

    /**
     * Adds an item
     * @param key
     * @param item
     */
    async addItem(key: string, item: ChangeQItem): Promise<any> {

        let _item: ChangeQItem;

        _item = item instanceof ChangeQItem ?
            item :
            ChangeQItemHelper.hydrate(item);

        _item = _item.update({
            position: this.nextItemPosition
        });

        await this.getStorage().set(key, _item);
        this.indexAdd(_item);
        await this.refreshCount();
    }

    /**
     * Updates an item
     * @param key
     * @param item
     */
    updateItem(key: string, item: ChangeQItem): Promise<any> {
        return this.getStorage().set(key, item);
    }

    /**
     * Returns an item
     * @param key
     */
    async getItem(key: string): Promise<ChangeQItem> {
        const item = await this.getStorage().get(key);

        if (this.isFileItem(item)) {
            return new FileChangeQItem(item);
        }

        if (this.isPostOrPatchReportLayoutItem(item)) {
            return new ReportLayoutChangeQItem(item);
        }

        return item ? new ChangeQItem(item) : undefined;
    }

    isFileItem(item): boolean {
        return item && item.entity && item.entity === 'file';
    }

    isPostOrPatchReportLayoutItem(item: ChangeQItem): boolean {
        return item && item.entity && item.entity === 'reportLayout' &&
            (item.method === 'POST' || item.method === 'PATCH');
    }

    /**
     * Returns all change q items sorted by created date
     */
    async getAllItems(): Promise<Array<ChangeQItem>> {
        const items = [];

        const keys = await this.keys();

        await asyncForEach(keys, async key => {
            const item = await this.getItem(key);
            items.push(item);
        });

        return items.sort((a: ChangeQItem, b: ChangeQItem) =>
            a.position - b.position
        );
    }

    /**
     * Returns items by their id (the object's, not the change item's)
     * @param id
     */
    async getItemsById(id: string): Promise<Array<ChangeQItem>> {

        const keys = this.getKeysById(id);

        if (!keys || !keys.length) {
            return [];
        }

        const items = [];

        await asyncForEach(keys, async key => {
            const item = await this.getItem(key);
            if (!item) {
                return;
            }
            items.push(item);
        });

        return items;
    }

    /**
     * Returns items by their parentId
     * @param id
     */
    async getItemsByParentId(id: string): Promise<Array<ChangeQItem>> {
        const items = await this.getAllItems();
        return items.filter(item => isDefined(item) && item.parentId === id);
    }

    /**
     * Returns the keys
     */
    keys(): Promise<Array<any>> {
        return this.getStorage().keys();
    }

    /**
     * Removes an item
     * @param key
     * @returns {Promise<any>}
     */
    async removeItem(key: string): Promise<void> {
        await this.getStorage().remove(key);
        await this.indexRemoveByKey(key);
        await this.refreshCount();
    }

    /**
     * Returns an observable which emits the number of items in the change q
     * @returns {Observable<number>}
     */
    getChangesWaiting(): Observable<number> {
        return Observable.fromPromise(this.refreshCount())
            .concatMap(() => ChangeLogService.changesWaiting$);
    }

    /**
     * Initialises the indexById value from the storage db
     */
    protected async indexInit(): Promise<void> {

        const items = await this.getAllItems();

        items.forEach(item => {
            this.updateItemCount(item);
            this.indexAdd(item);
        });
    }

    /**
     * Adds a change q item key to the index
     * @param changeItem
     */
    protected indexAdd(changeItem: ChangeQItem): void {

        if (!isDefined(this.indexById[changeItem.id])) {
            this.indexById[changeItem.id] = [];
        }

        this.indexById[changeItem.id].push(changeItem.key);
    }

    /**
     * Removes a change q item key from the index
     * @param changeItem ChangeQItem
     */
    protected indexRemove(changeItem: ChangeQItem): void {
        this.indexRemoveByKey(changeItem.key);
    }

    /**
     * Removes a change q item key from the index
     * @param key string
     */
    protected indexRemoveByKey(key: string): void {
        let id;
        let pos;

        const keys = Object.keys(this.indexById);

        pos = keys.findIndex(_id =>
            isInArray(key, this.indexById[_id])
        );

        if (pos < 0) {
            return;
        }

        id = keys[pos];

        this.indexById[id] = this.indexById[id].filter(_key => _key !== key);
    }

    /**
     * Clears the index
     */
    protected clearIndex(): void {
        this.indexById = {};
    }

    /**
     * Returns the change q item keys associated with the object id
     * @param id
     * @returns {any}
     */
    getKeysById(id: string): Array<string> {

        if (!isDefined(this.indexById[id])) {
            return [];
        }

        return this.indexById[id] || [];
    }

    /**
     * Returns true if there is a change q item for the specified object id
     * @param id
     * @returns {boolean}
     */
    isChangePending(id: string): boolean {
        return this.getKeysById(id).length > 0;
    }

    /**
     * Updates the itemPosition value if the position value of the change item
     * is higher
     * @param item
     */
    protected updateItemCount(item: ChangeQItem): void {
        this._itemPosition = Math.max(this._itemPosition, item.position);
    }
}



// WEBPACK FOOTER //
// ./src/core/services/change-log/changelog.service.ts